import { RequisitionModelSerializer } from "../recoil/types";

const isComplete = (status?: string): boolean =>
  status === "Final" || status === "Corrected";

const isIncomplete = (status?: string): boolean =>
  status === "Processing" || status === "Ordered";

const isHighRiskOrPositive = (summary?: string): boolean =>
  summary === "See Report";

/**
 * Determines if a patient should be blocked from booking a genetic counseling appointment.
 *
 * Blocking criteria:
 * - Single test scenario: If only one test was ordered and it's not complete (Processing/Ordered status)
 * - Both tests scenario: If both tests ordered, at least one is low risk/negative, and at least one is incomplete
 *
 * Allow booking when:
 * - High risk/positive results (summary: "See Report")
 * - All ordered tests are complete (status: "Final" or "Corrected")
 *
 * @param requisition - The requisition object containing test order and result information
 * @returns true if booking should be blocked, false if booking is allowed
 */
export function shouldBlockGCBooking(
  requisition?: RequisitionModelSerializer,
): boolean {
  if (!requisition) {
    return false;
  }

  const carrierOrdered = requisition.is_unity_included ?? false;
  const aneuploidyOrdered = requisition.is_aneuploidy_included ?? false;

  // Get the first report from each array (should only be one)
  const carrierReport = requisition.unity_reports?.[0];
  const aneuploidyReport = requisition.aneuploidy_reports?.[0];

  if (carrierOrdered && aneuploidyOrdered) {
    const carrierStatus = carrierReport?.status;
    const aneuploidyStatus = aneuploidyReport?.status;
    const carrierSummary = carrierReport?.summary;
    const aneuploidySummary = aneuploidyReport?.summary;

    if (isComplete(carrierStatus) && isComplete(aneuploidyStatus)) {
      return false;
    }

    // If any test is incomplete, check if we have a high risk result that's already finalized
    // Only allow booking if there's a FINALIZED high risk result
    const hasFinalizedHighRisk =
      (isComplete(carrierStatus) && isHighRiskOrPositive(carrierSummary)) ||
      (isComplete(aneuploidyStatus) && isHighRiskOrPositive(aneuploidySummary));

    if (hasFinalizedHighRisk) {
      return false;
    }

    // Block if any test is incomplete
    if (isIncomplete(carrierStatus) || isIncomplete(aneuploidyStatus)) {
      return true;
    }

    return false;
  }

  if (carrierOrdered && !aneuploidyOrdered) {
    const carrierStatus = carrierReport?.status;
    const carrierSummary = carrierReport?.summary;

    if (isComplete(carrierStatus) && isHighRiskOrPositive(carrierSummary)) {
      return false;
    }

    if (isIncomplete(carrierStatus)) {
      return true;
    }

    return false;
  }

  if (aneuploidyOrdered && !carrierOrdered) {
    const aneuploidyStatus = aneuploidyReport?.status;
    const aneuploidySummary = aneuploidyReport?.summary;

    if (
      isComplete(aneuploidyStatus) &&
      isHighRiskOrPositive(aneuploidySummary)
    ) {
      return false;
    }

    if (isIncomplete(aneuploidyStatus)) {
      return true;
    }

    return false;
  }

  return false;
}
